import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { GetGiveawayDataResponseType } from '@/entities';
import { telegramClientApi } from '@/shared';

export interface IGiveawayState {
  giveawayData: GetGiveawayDataResponseType | null;
  loading: boolean;
  error: string | null;
  fetchError: string | null;
}

const initialState: IGiveawayState = {
  giveawayData: null,
  loading: false,
  error: null,
  fetchError: null
};

export const fetchGiveawayData = createAsyncThunk<
  GetGiveawayDataResponseType | null,
  void,
  { rejectValue: string }
>('giveaway/fetchGiveawayData', async (_: void, { rejectWithValue }) => {
  try {
    const res = await telegramClientApi.getGiveawayData();

    if (res.error) return rejectWithValue(res.error.message);

    return res.data ?? null;
  } catch (e: any) {
    return rejectWithValue(e?.message ?? 'Failed to fetch giveaway data');
  }
});

const giveawaySlice = createSlice({
  name: 'giveaway',
  initialState,
  reducers: {
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
    clearError(state) {
      state.error = null;
    },
    clearSearch(state) {
      state.loading = false;
      state.error = null;
    },
    clearGiveawayData(state) {
      state.giveawayData = null;
      state.loading = false;
      state.error = null;
    },
    clearAll(state) {
      state.giveawayData = null;
      state.loading = false;
      state.error = null;
    },
    setGiveawayData(state, action: PayloadAction<GetGiveawayDataResponseType | null>) {
      state.giveawayData = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGiveawayData.pending, (state) => {
        state.loading = true;
        state.fetchError = null;
      })
      .addCase(fetchGiveawayData.fulfilled, (state, action) => {
        state.loading = false;
        state.fetchError = null;
        state.giveawayData = action.payload;
      })
      .addCase(fetchGiveawayData.rejected, (state, action) => {
        state.loading = false;
        state.giveawayData = null;
        state.fetchError =
          action.payload ?? action.error.message ?? 'Failed to fetch giveaway data';
      });
  }
});

export const { setError, clearError, clearSearch, clearGiveawayData, clearAll, setGiveawayData } =
  giveawaySlice.actions;

export default giveawaySlice.reducer;
